/**
 * What each plan is, in the words a subscriber reads: one copy for the
 * pricing page, the dashboard's Settings and the upgrade cards, so the three
 * cannot promise different things. Shared with the SPA, so nothing here may
 * import server code.
 *
 * Only what is built is listed. A line here is a promise the gates in
 * plan.ts keep: add the feature first, then the line.
 *
 * The words stay on the "information service" side of the line the site
 * draws for itself: alerts, filings, figures as stated. No picks, ratings or
 * urgency.
 */
import {
  FEATURE_PLAN,
  PLAN_NAMES,
  RULE_LIMIT,
  type Feature,
  type Plan,
} from './plan';

export interface PlanOffer {
  plan: Plan;
  name: string;
  /** One line: what the plan is for. */
  line: string;
  /** What it includes; a paid plan's first line names the plan below it. */
  features: string[];
}

export const OFFERS: Record<Plan, PlanOffer> = {
  FREE: {
    plan: 'FREE',
    name: PLAN_NAMES.FREE,
    line: 'The week’s new raises in one email.',
    features: [
      'Weekly Sonar email',
      'Feed, 24–48 hours behind',
      'Region and status filters',
      'Eligibility for your country on every raise',
    ],
  },
  ALERTS: {
    plan: 'ALERTS',
    name: PLAN_NAMES.ALERTS,
    line: 'Know the day a matching raise opens, and the days before it closes.',
    features: [
      'Live feed, as each platform is checked',
      'Launch alerts by email, within minutes',
      'Closing reminders at 7 days, 72 hours and 24 hours',
      'Near-cap, perk-expiry and term-change alerts',
      // Listed ahead of the build at Aleks's request (28 Oct 2026): no
      // momentum alarm is sent yet.
      'Momentum alarms on raises you follow',
      `Up to ${RULE_LIMIT.ALERTS} alert rules, with every filter`,
      'Watchlist and portfolio',
      'Calendar with ICS export',
    ],
  },
  PRO: {
    plan: 'PRO',
    name: PLAN_NAMES.PRO,
    line: 'The numbers behind every raise, ready for your own diligence.',
    features: [
      `Everything in ${PLAN_NAMES.ALERTS}`,
      'Stated financials, from the filing or the listing',
      // History and similar raises are built; sort by pace is not yet
      // (28 Oct 2026).
      'Momentum history, similar-raise comparisons and sort by pace',
      'Excel exports of the feed and of any raise',
      'Watchlist CSV export',
      `Up to ${RULE_LIMIT.PRO} alert rules`,
    ],
  },
};

/** What an upgrade card says where a feature is locked. */
export interface Pitch {
  title: string;
  body: string;
}

export const PITCHES: Record<Feature, Pitch> = {
  LIVE_FEED: {
    title: 'See raises the day they open',
    body: 'On Free the feed runs 24 to 48 hours behind. Alerts shows each raise as soon as its platform is checked.',
  },
  ALL_FILTERS: {
    title: 'Filter by sector, country, platform and more',
    body: 'Free filters by region and status. Alerts adds sector, issuer country, platform, regime, security type and “open to me”.',
  },
  ALERTS: {
    title: 'Hear when a raise opens, and before it closes',
    body: 'Alert rules email you at launch, 7 days, 72 hours and 24 hours before close, near the cap and before a perk ends.',
  },
  WATCHLIST: {
    title: 'Follow the raises you care about',
    body: 'Watch a raise to get its closing reminders, and keep what you invested in one place.',
  },
  CALENDAR: {
    title: 'Every deadline on one calendar',
    body: 'Month and week views of closing dates, with an ICS file for your own calendar.',
  },
  FINANCIALS: {
    title: 'Stated financials',
    body: 'Revenue, net income, cash and debt as the filing or the listing states them, most recent year beside the one before.',
  },
  MOMENTUM: {
    title: 'Momentum and similar raises',
    body: 'How fast this raise is filling day by day, and what else like it is live.',
  },
  EXPORT: {
    title: 'Export to Excel',
    body: 'The feed, a raise or your watchlist as a workbook you can sort, filter and keep.',
  },
};

/** The plan an upgrade card for `feature` offers, by name: "Alerts", "Pro". */
export function pitchPlan(feature: Feature): string {
  return PLAN_NAMES[FEATURE_PLAN[feature]];
}
